import { monthsToYears } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { License } from '../../../stores/types';
import { formatDate } from '../../../utils/Date.Util';
import {
  LICENSE_STATUS,
  daysDifferenceText,
  getLicenseType,
  isNoDurationType,
} from '../../Licenses/utils';
import { ColorType, Label, LabelType } from '../../_ui';
import LicenseLineItem from './LicenseLineItem.component';

interface IProps {
  license: License | null;
  isLoading: boolean;
}
const ChargersLicenseDetails = ({ license, isLoading }: IProps) => {
  const { t } = useTranslation();

  const portKeys = useMemo(() => {
    if (!license?.ports || license.ports === 1) return null;

    return Array.from({ length: license.ports }, (_, index) => (
      <Label
        key={index}
        text={`${t('port')} ${index + 1} key: ${license.licenseKey}.${index + 1}`}
        type={LabelType.BODY4}
        color={ColorType.GREY6}
      />
    ));
  }, [license?.licenseKey, license?.ports, t]);

  const formatRemainingDays = (endDate: string | Date) => {
    if (license?.status?.toLowerCase() === LICENSE_STATUS.EXPIRED) {
      return t('expired_license_days', {
        noOfYearsDays: daysDifferenceText(true, endDate, t),
      });
    }
    return t('license_remaining_days', {
      noOfYearsDays: daysDifferenceText(false, endDate, t),
    });
  };

  const renderLicenseKeyValue = () => (
    <div className='flex flex-col gap-0.5'>
      <Label
        text={license?.licenseKey}
        type={LabelType.BODY3}
        color={ColorType.BLACK}
        isLoading={isLoading}
        skeletonWidth='w-52'
        skeletonHeight='h-5'
      />
      {portKeys}
    </div>
  );

  const renderLicenseKey = () => (
    <LicenseLineItem
      isLoading={isLoading}
      label={t('license_key')}
      value={renderLicenseKeyValue()}
    />
  );

  const renderLicenseDuration = () => {
    const licenseDurationText = license
      ? t('license_duration_year_license', {
          duration: monthsToYears(license?.duration),
        })
      : '';
    return (
      <LicenseLineItem
        label={t('license_duration')}
        value={licenseDurationText}
        isLoading={isLoading}
      />
    );
  };

  const licenseExpiresValue = () => {
    const expiryDate = license?.expirationDate ? formatDate(new Date(license.expirationDate)) : '';
    return (
      <div className='flex flex-col gap-0.5'>
        <Label
          text={expiryDate}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          isLoading={isLoading}
          skeletonWidth='w-52'
          skeletonHeight='h-5'
        />
        <Label
          text={license?.expirationDate ? formatRemainingDays(license.expirationDate) : ''}
          type={LabelType.BODY3}
          color={ColorType.GREY6}
        />
      </div>
    );
  };

  const renderLicenseExpires = () => (
    <LicenseLineItem
      label={t('license_expires')}
      isLoading={isLoading}
      value={licenseExpiresValue()}
    />
  );

  const licenseType = useMemo(() => {
    return license?.type ? getLicenseType(license?.type) : '';
  }, [license?.type]);

  const renderLicenseType = () => (
    <LicenseLineItem
      label={t('license_type_placeholder')}
      isLoading={isLoading}
      value={license?.type ? `${t(licenseType)}, ${license?.ports}-${t('port')}` : ''}
    />
  );

  return (
    <>
      {renderLicenseKey()}
      {renderLicenseType()}
      {!isNoDurationType(licenseType, license?.includes4G) && renderLicenseDuration()}
      {!isNoDurationType(licenseType, license?.includes4G) && renderLicenseExpires()}
    </>
  );
};

export default ChargersLicenseDetails;
